/**
 * Trip Detail Page Component
 * /admin/trips/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StopIcon from '@mui/icons-material/Stop';
import apiClient from '../api/client';

const TripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [endOdometer, setEndOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadTrip();
  }, [id]);

  useEffect(() => {
    if (stopDialogOpen && trip?.vehicle_detail?.odometer) {
      // Set default end odometer from vehicle if available
      setEndOdometer(trip.vehicle_detail.odometer.toString());
    }
  }, [stopDialogOpen, trip]);

  const loadTrip = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getTrip(id);
      setTrip(data);
    } catch (err) {
      setError(err.message || 'Failed to load trip details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'info',
      in_progress: 'primary',
      completed: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleStopTrip = async () => {
    try {
      setStopping(true);
      setError('');
      const response = await apiClient.stopTrip(
        id,
        endOdometer ? parseInt(endOdometer) : null,
        notes
      );
      setSuccessMessage('Trip stopped successfully!');
      setStopDialogOpen(false);
      setEndOdometer('');
      setNotes('');
      // Reload trip to get updated data
      await loadTrip();
    } catch (err) {
      setError(err.message || 'Failed to stop trip');
    } finally {
      setStopping(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !trip) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/trips')}>
          Back to Trips
        </Button>
      </Container>
    );
  }

  if (!trip) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">Trip not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/trips')}>
          Back to Trips
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/trips')}
            sx={{ mb: 1 }}
          >
            Back to Trips
          </Button>
          <Typography variant="h4" component="h1">
            Trip Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trip ID: {trip.id.substring(0, 8)}
          </Typography>
        </Box>
        {trip.status === 'in_progress' && (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopIcon />}
            onClick={() => setStopDialogOpen(true)}
          >
            Stop Trip
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Status & Basic Info */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Status & Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={trip.status || 'scheduled'}
                  color={getStatusColor(trip.status)}
                  sx={{ mt: 0.5 }}
                />
              </Box>
              {trip.booking_detail && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Booking Reference:
                  </Typography>
                  <Typography variant="body1">
                    {trip.booking_detail.booking_reference || '-'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Vehicle Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Vehicle Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              {trip.vehicle_detail ? (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Plate Number:
                    </Typography>
                    <Typography variant="body1">{trip.vehicle_detail.plate_number || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Make & Model:
                    </Typography>
                    <Typography variant="body1">
                      {trip.vehicle_detail.make} {trip.vehicle_detail.model} ({trip.vehicle_detail.year || 'N/A'})
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No vehicle assigned
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Driver Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Driver Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              {trip.driver_detail ? (
                <>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Driver Name:
                    </Typography>
                    <Typography variant="body1">{trip.driver_detail.full_name || '-'}</Typography>
                  </Box>
                  {trip.driver_detail.phone && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Phone:
                      </Typography>
                      <Typography variant="body1">{trip.driver_detail.phone}</Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No driver assigned
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Trip Timing */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Trip Timing
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Started At:
                </Typography>
                <Typography variant="body1">{formatDate(trip.started_at)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Completed At:
                </Typography>
                <Typography variant="body1">{formatDate(trip.completed_at)}</Typography>
              </Box>
              {trip.scheduled_start_time && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled Start:
                  </Typography>
                  <Typography variant="body1">{formatDate(trip.scheduled_start_time)}</Typography>
                </Box>
              )}
              {trip.scheduled_end_time && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled End:
                  </Typography>
                  <Typography variant="body1">{formatDate(trip.scheduled_end_time)}</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Odometer & Distance */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Odometer & Distance
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Start Odometer:
                </Typography>
                <Typography variant="body1">
                  {trip.start_odometer ? `${trip.start_odometer.toLocaleString()} km` : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  End Odometer:
                </Typography>
                <Typography variant="body1">
                  {trip.end_odometer ? `${trip.end_odometer.toLocaleString()} km` : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Actual Distance:
                </Typography>
                <Typography variant="body1">
                  {trip.actual_distance != null && typeof trip.actual_distance === 'number'
                    ? `${trip.actual_distance.toFixed(2)} km`
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Financial Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Financial Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Actual Cost:
                </Typography>
                <Typography variant="body1">
                  {trip.actual_cost != null && typeof trip.actual_cost === 'number'
                    ? `AED ${trip.actual_cost.toFixed(2)}`
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Revenue Amount:
                </Typography>
                <Typography variant="body1">
                  {trip.revenue_amount != null && typeof trip.revenue_amount === 'number'
                    ? `AED ${trip.revenue_amount.toFixed(2)}`
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Invoice Generated:
                </Typography>
                <Chip
                  label={trip.invoice_generated ? 'Yes' : 'No'}
                  color={trip.invoice_generated ? 'success' : 'default'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Booking Details Link */}
        {trip.booking && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Related Booking
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/bookings/${trip.booking}`)}
              >
                View Booking Details
              </Button>
            </Paper>
          </Grid>
        )}

        {/* Notes */}
        {trip.notes && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">{trip.notes}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Stop Trip Dialog */}
      <Dialog
        open={stopDialogOpen}
        onClose={() => setStopDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Stop Trip</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Stop this trip and mark it as completed. The system will update the booking status and calculate the actual distance traveled.
          </DialogContentText>
          <TextField
            fullWidth
            label="End Odometer (Optional)"
            type="number"
            value={endOdometer}
            onChange={(e) => setEndOdometer(e.target.value)}
            placeholder="Enter ending odometer reading..."
            helperText={
              trip?.vehicle_detail?.odometer
                ? `Vehicle current odometer: ${trip.vehicle_detail.odometer.toLocaleString()} km`
                : trip?.start_odometer
                ? `Trip started at: ${trip.start_odometer.toLocaleString()} km`
                : ''
            }
            slotProps={{ htmlInput: { min: 0 } }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about the trip completion..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopDialogOpen(false)} disabled={stopping}>
            Cancel
          </Button>
          <Button
            onClick={handleStopTrip}
            variant="contained"
            color="error"
            disabled={stopping}
            startIcon={stopping ? <CircularProgress size={20} /> : <StopIcon />}
          >
            {stopping ? 'Stopping...' : 'Stop Trip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TripDetail;

